import { useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2 } from "lucide-react";

const UploadRow = ({ token, tipo, label, caricato, onDone }) => {
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("tipo", tipo); fd.append("file", file);
    try { const { data } = await api.post(`/public/accordi/preventivi/${token}/documento`, fd); onDone(data.caricati); toast.success("Documento caricato"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm hover:bg-sage/40" data-testid={`verifica-upload-${tipo}`}>
      <span className="text-forest">{label}</span>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : caricato ? <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid={`verifica-ok-${tipo}`} /> : <FileUp className="h-4 w-4 text-terracotta" />}
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => up(e.target.files?.[0])} data-testid={`verifica-file-${tipo}`} />
    </label>
  );
};

// Verifica del diritto: categoria + documenti probatori oppure autocertificazione con documento e firma OTP.
export const VerificaDiritto = ({ p, onInviata }) => {
  const [categoria, setCategoria] = useState(p.categoria || "");
  const [altro, setAltro] = useState("");
  const [modalita, setModalita] = useState(p.modalita || "documenti");
  const [caricati, setCaricati] = useState(p.documenti_caricati || []);
  const [dich, setDich] = useState(false);
  const [cell, setCell] = useState(p.cliente?.telefono || "");
  const [otp, setOtp] = useState("");
  const [otpDest, setOtpDest] = useState("");
  const [busy, setBusy] = useState(false);
  const docs = p.documenti[modalita];

  const inviaOtp = async () => {
    try { const { data } = await api.post(`/public/accordi/preventivi/${p.token}/otp`, { cellulare: cell }); setOtpDest(data.destinazione); toast.success(`Codice inviato a ${data.destinazione}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const invia = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/accordi/preventivi/${p.token}/verifica`, { categoria, categoria_altro: altro, modalita, dichiarazione_accettata: dich, otp_code: otp });
      toast.success(data.message); onInviata();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const pronti = Object.keys(docs).every((t) => caricati.includes(t)) && categoria && (modalita === "documenti" || (dich && otp.length >= 4));

  return (
    <div className="space-y-5" data-testid="verifica-diritto">
      <div className="space-y-1.5">
        <Label>Come fai parte dell'ente?</Label>
        <div className="flex flex-wrap gap-2">
          {p.beneficiari.map((b) => (
            <button key={b.value} onClick={() => setCategoria(b.value)} data-testid={`verifica-categoria-${b.value}`}
              className={`rounded-full border px-3 py-1.5 text-sm ${categoria === b.value ? "border-forest bg-forest text-white" : "border-border text-forest"}`}>{b.label}</button>
          ))}
        </div>
        {categoria === "altro" && <Input value={altro} onChange={(e) => setAltro(e.target.value)} placeholder="Specifica" data-testid="verifica-categoria-altro" />}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {[["documenti", "Carico un documento", "Badge, statino, certificazione o attestazione dell'ente"], ["autocertificazione", "Autocertificazione", "Dichiarazione sostitutiva firmata con codice OTP + documento d'identità"]].map(([v, t, d]) => (
          <button key={v} onClick={() => setModalita(v)} data-testid={`verifica-modalita-${v}`}
            className={`rounded-xl border-2 p-3 text-left ${modalita === v ? "border-terracotta bg-terracotta/5" : "border-border/60"}`}>
            <div className="font-medium text-forest">{t}</div><div className="text-xs text-muted-foreground">{d}</div>
          </button>
        ))}
      </div>
      <div className="space-y-2">{Object.entries(docs).map(([t, l]) => <UploadRow key={t} token={p.token} tipo={t} label={l} caricato={caricati.includes(t)} onDone={setCaricati} />)}</div>
      {modalita === "autocertificazione" && (
        <div className="space-y-3 rounded-xl border bg-sage/30 p-4 text-sm">
          <label className="flex items-start gap-2 text-forest">
            <Checkbox checked={dich} onCheckedChange={(v) => setDich(!!v)} data-testid="verifica-dichiarazione" />
            Dichiaro, ai sensi degli artt. 46 e 47 del D.P.R. 445/2000 e consapevole delle sanzioni per dichiarazioni mendaci (art. 76), di appartenere all'ente {p.accordo_nome} nella categoria indicata e di avere diritto alle condizioni dell'accordo {p.codice}.
          </label>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label>Cellulare per la firma</Label><Input value={cell} onChange={(e) => setCell(e.target.value)} data-testid="verifica-cellulare" className="w-48" /></div>
            <Button variant="outline" onClick={inviaOtp} disabled={!cell} data-testid="verifica-invia-otp">Invia codice</Button>
            <div className="space-y-1"><Label>Codice OTP</Label><Input value={otp} onChange={(e) => setOtp(e.target.value.trim())} data-testid="verifica-otp" className="w-32 font-mono" /></div>
          </div>
          {otpDest && <p className="text-xs text-muted-foreground">Codice inviato a {otpDest}.</p>}
        </div>
      )}
      <Button onClick={invia} disabled={!pronti || busy} data-testid="verifica-invia" className="w-full bg-terracotta hover:bg-terracotta-hover">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Invia per la verifica e procedi</Button>
    </div>
  );
};
